// Finding a place by typing its name.
//
// The corpus is what this map already knows: the pins from /meta (stations,
// the bus station, the airport, ferry terminals), whatever live features are
// currently drawn (cameras, roadworks, incidents), and the baked stop list.
// No geocoder, so no usage policy, no attribution clause, and no dependency on
// somebody else's uptime for the search box to work.
//
// No fuzzy-matching library either. minisearch is 7 kB and would double this
// project's runtime dependencies, which stand at one — and for a few thousand
// short British street names, prefix and substring matching answers the real
// queries. Typo tolerance sounds free and is not: "Union" fuzzily matches
// "Unicorn" and "Onion", and a search box that offers confident wrong answers
// is worse than one that offers none. If it is ever wanted, it wants measuring
// against real queries first.
//
// Ranking is by HOW the query matched, then by distance from where the reader
// is looking. The second half matters more than it sounds: a city has a dozen
// stops called High Street, and the one being asked about is nearly always the
// nearest one.
//
// This file is pure — no DOM, no fetch — so the ranking can be argued with in
// a test rather than in a browser.

/** What kind of thing a hit is. Decides its glyph and its tiebreak weight. */
export type SearchKind =
  | 'station'   // a rail station from the place pack
  | 'place'     // any other /meta pin: airport, bus station, ferry terminal
  | 'stop'      // a baked bus stop
  | 'tram'
  | 'subway'
  | 'route'     // a bus route, by its number
  | 'feature';  // anything the live layers are drawing

export interface SearchItem {
  readonly id: string;
  /** What the reader sees, and the main thing matched against. */
  readonly name: string;
  readonly kind: SearchKind;
  /**
   * Where it is — absent for the things that are not anywhere.
   *
   * A bus route is a line across a city and has no single point, and the
   * picker's list carries no geometry to derive one from. Optional rather than
   * faked: a made-up centre would sort routes by a distance that means
   * nothing, and NaN would poison the score and scatter them through the list
   * unpredictably. rank() treats a positionless item as having no distance
   * rather than as being infinitely far, because "the X8" is equally the
   * answer wherever the map happens to be pointing.
   */
  readonly lat?: number;
  readonly lon?: number;
  /**
   * The layer this belongs to, where it has one.
   *
   * Carried so the caller can switch that layer on before flying to the hit.
   * main.ts learned this the hard way with the Now box — focusIncident() looks
   * the id up in what the map is *currently drawing*, so a row for a feature
   * on a switched-off layer did nothing at all. A search result is the same
   * shape of promise and would break the same way.
   */
  readonly providerId?: string;
  /** Matched but not displayed: a CRS code, a road number, an operator. */
  readonly aka?: readonly string[];
  /** A short qualifier shown beneath the name — "rail station", "camera". */
  readonly detail?: string;
}

export interface SearchResult {
  readonly item: SearchItem;
  /** Lower is better. Exposed so a test can argue about the order. */
  readonly score: number;
}

/**
 * Down to comparable letters.
 *
 * Diacritics are folded because a reader typing "Bellahouston" should not have
 * to reproduce anything they cannot see, and punctuation goes because stop
 * names are full of it — "Union Street (Stance 3)", "St. Enoch", "King's
 * Cross". Whitespace collapses so a double space never hides a match.
 */
export function normalise(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    // Apostrophes vanish rather than becoming a space, and every spelling of
    // one goes: a reader types "kings cross", the data says "King's Cross",
    // and a space would leave those as `kings cross` and `king s cross` — no
    // match, no error, no clue. Scotland is full of the case: King's, Queen's,
    // St Machar's, the Provost's, Bo'ness. Curly and modifier-letter forms are
    // here because real feeds carry both and a search box cannot ask which.
    .replace(/['\u2018\u2019\u02bc\u0060]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** How well one string answers the query. Lower is better; null is no match. */
function matchTier(haystack: string, needle: string): number | null {
  if (!haystack) return null;
  if (haystack === needle) return 0;
  if (haystack.startsWith(needle)) return 1;
  // A word inside the name starting with the query: "street" finding "Union
  // Street", which is how people search for a stop whose first word they have
  // forgotten.
  if (haystack.includes(` ${needle}`)) return 2;
  if (haystack.includes(needle)) return 3;
  return null;
}

/**
 * Kind weight, as a tiebreak between equally good text matches.
 *
 * A rail station beats a bus stop of the same name because it is the bigger
 * landmark and the likelier intent — somebody typing "Partick" in Glasgow
 * means the station far more often than the shelter outside it. Live features
 * come last: they are transient, and a reader searching a street name wants
 * the street, not the roadworks that happen to be on it today.
 */
const KIND_WEIGHT: Record<SearchKind, number> = {
  route: 0, station: 0, place: 1, subway: 1, tram: 2, stop: 3, feature: 4,
};

/** Rough kilometres between two points. Good enough to sort by. */
function distanceKm(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const dLat = (bLat - aLat) * 111;
  const dLon = (bLon - aLon) * 111 * Math.cos((aLat * Math.PI) / 180);
  return Math.hypot(dLat, dLon);
}

export interface RankOptions {
  /** Where the reader is looking. Nearer hits win an otherwise equal match. */
  readonly centre?: { lat: number; lon: number };
  readonly limit?: number;
}

/**
 * The best matches for a query, best first.
 *
 * The composite score is tier first and everything else as decreasing
 * fractions of one tier, so a better *kind* of match always beats a nearer
 * one. That order is deliberate: a reader who typed an exact name and got the
 * nearest partial match instead would think the search was broken, whereas one
 * who got the further of two exact matches just scrolls.
 */
export function rank(
  items: readonly SearchItem[],
  query: string,
  { centre, limit = 8 }: RankOptions = {},
): SearchResult[] {
  const needle = normalise(query);
  if (needle.length === 0) return [];
  /**
   * One character, exact matches only.
   *
   * Two was the flat minimum, on the reasoning that one character matches most
   * of a city and answers nothing — which is true of names and false of route
   * numbers. Aberdeen runs a 1, a 2, a 3 and an 8, and under the flat rule
   * every one of them was unsearchable: typing "3" returned nothing at all,
   * with no way for the reader to tell that from the route not existing.
   *
   * Exact-only is the whole fix. "3" offers the route 3 and nothing else,
   * because a single character cannot start or be inside a name without
   * matching half the city, and tier 0 is the only tier that means "this is
   * the thing itself". An alias cannot reach tier 0 by construction, so a
   * one-character query never resolves through one either.
   */
  const exactOnly = needle.length < 2;

  const results: SearchResult[] = [];

  for (const item of items) {
    let tier = matchTier(normalise(item.name), needle);

    // An alias only ever matches as well as a middle-of-the-name hit would.
    // "ABD" finding Aberdeen station is worth having; it should not outrank
    // somewhere actually called ABD.
    if (tier === null && item.aka?.length) {
      for (const alias of item.aka) {
        const aliasTier = matchTier(normalise(alias), needle);
        if (aliasTier !== null) { tier = Math.min(aliasTier + 2, 4); break; }
      }
    }
    if (tier === null) continue;
    if (exactOnly && tier !== 0) continue;

    // Nearer is better, saturating at 50 km so that two hits in the same city
    // are separated by their distance while two in different counties are not
    // ordered by an arbitrarily large number.
    const km = centre && item.lat !== undefined && item.lon !== undefined
      ? distanceKm(centre.lat, centre.lon, item.lat, item.lon)
      : 0;
    const nearness = Math.min(km, 50) / 50;
    // Shorter names last of all: "Union Street" over "Union Street Stance 3"
    // when everything else is equal, because the shorter one is the thing
    // itself rather than one of its parts.
    const brevity = Math.min(item.name.length, 60) / 60;

    results.push({
      item,
      score: tier + (KIND_WEIGHT[item.kind] / 10) + (nearness / 100) + (brevity / 1000),
    });
  }

  results.sort((a, b) => a.score - b.score || a.item.name.localeCompare(b.item.name));
  return results.slice(0, limit);
}

/**
 * Stops that share a name folded into one hit.
 *
 * A city street has a stop on each side and often several stances, all called
 * the same thing. Eight results that are one street read as a broken search
 * and crowd out everywhere else the reader might have meant. The nearest of
 * each name survives — which is also the one they probably want.
 *
 * Only stops: two rail stations with the same name are two different places,
 * and merging them would lose one.
 */
export function dedupeStops(
  items: readonly SearchItem[],
  centre?: { lat: number; lon: number },
): SearchItem[] {
  const best = new Map<string, SearchItem>();
  const out: SearchItem[] = [];

  for (const item of items) {
    if (item.kind !== 'stop') { out.push(item); continue; }
    const key = normalise(item.name);
    const incumbent = best.get(key);
    if (!incumbent) { best.set(key, item); continue; }
    if (!centre) continue;
    const near = (s: SearchItem) =>
      (s.lat === undefined || s.lon === undefined
        ? Infinity
        : distanceKm(centre.lat, centre.lon, s.lat, s.lon));
    if (near(item) < near(incumbent)) best.set(key, item);
  }

  return [...out, ...best.values()];
}
